// src/pages/Payments/PaymentsPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import api from '../../services/api';

const PaymentsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transactionsData, balanceData] = await Promise.all([
        api.get('/payments/transactions/').catch(() => ({ results: [] })),
        api.get('/payments/balances/my_balance/').catch(() => null),
      ]);
      setTransactions(transactionsData.results || []);
      setBalance(balanceData);
    } catch (err) {
      setError(err.data?.message || 'Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Payments & Billing
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Balance Card for Suppliers */}
      {user?.user_type === 'supplier' && balance && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Your Balance
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Available Balance
              </Typography>
              <Typography variant="h5">
                {balance.currency} {balance.available_balance?.toFixed(2) || '0.00'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Pending Balance
              </Typography>
              <Typography variant="h5">
                {balance.currency} {balance.pending_balance?.toFixed(2) || '0.00'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total Earned
              </Typography>
              <Typography variant="h5">
                {balance.currency} {balance.total_earned?.toFixed(2) || '0.00'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Transactions" />
          <Tab label="Payment Methods" />
          <Tab label="Invoices" />
        </Tabs>
      </Box>

      {/* Transactions Tab */}
      {tabValue === 0 && (
        <Grid container spacing={2}>
          {transactions.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No transactions yet
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/solutions')}
                >
                  Browse Solutions
                </Button>
              </Paper>
            </Grid>
          ) : (
            transactions.map((tx) => (
              <Grid item xs={12} key={tx.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {tx.transaction_reference}
                        </Typography>
                        <Typography variant="body1">
                          {tx.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(tx.transaction_date).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6">
                          {tx.currency} {tx.amount?.toFixed(2)}
                        </Typography>
                        <Chip
                          label={tx.status}
                          size="small"
                          color={tx.status === 'completed' ? 'success' : 
                                tx.status === 'pending' ? 'warning' : 
                                tx.status === 'failed' ? 'error' : 'default'}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Payment Methods Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Payment Methods
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                You haven't added any payment methods yet.
              </Typography>
              <Button
                variant="contained"
                disabled
              >
                Add Payment Method (Coming Soon)
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Invoices Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Invoices
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                No invoices available.
              </Typography>
              <Button
                variant="contained"
                disabled
              >
                View Invoices (Coming Soon)
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default PaymentsPage;